import * as yup from "yup";

import {
  AbsoluteCenter,
  Alert,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  Image,
  LinkProps,
  Text,
  TextProps,
  FlexProps,
} from "@chakra-ui/react";
import { AppForm, FormField, FormFieldProps, Link } from "@picovico/ui";
import {
  catchErrorHandler,
  parseApiError,
  storeLocalStorageData,
} from "@picovico/utils";
import { useLoginUserMutation } from "store/services/auth";

import { useAppToast } from "@/hooks/useAppToast";
import { useSocialSignIn } from "@/hooks/useSocialSignIn";
import { useVisibleSocialProviders } from "@/hooks/useWebFeature";
import { getPageRoutes } from "@/routes/pageRoutes";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useAppDispatch } from "store";
import { resetUserStateAction } from "store/slices";

export interface loginValues {
  email: string;
  password: string;
}

const loginFormValidationSchema = yup.object().shape({
  email: yup.string().email().required().label("Email"),
  password: yup.string().required().label("Password"),
});

const initialValues: loginValues = {
  email: "",
  password: "",
};

type SocialAuthProvider = { providerName: string; providerSvg: string };

interface LoginFormProps extends BoxProps {
  socialAuthProviders?: SocialAuthProvider[];
  buttonProps?: ButtonProps;
  formProps?: BoxProps;
  forgotPasswordProps?: LinkProps;
  formFieldProps?: FormFieldProps;
  socialButtonProps?: ButtonProps;
  showSocialProviderLabel?: boolean;
  hideRegisterLink?: boolean;
  inlineActions?: boolean;
  actionRowProps?: FlexProps;
  orDividerText?: string;
  orDividerProps?: BoxProps;
  emailFieldProps?: FormFieldProps;
  passwordFieldProps?: FormFieldProps;
  registerLinkTextProps?: TextProps;
  registerLinkProps?: LinkProps;
}

function LoginForm({
  socialAuthProviders: configuredSocialProviders,
  buttonProps,
  formProps,
  formFieldProps,
  forgotPasswordProps,
  socialButtonProps,
  showSocialProviderLabel = false,
  hideRegisterLink = false,
  inlineActions = false,
  actionRowProps,
  orDividerText = "Or Continue With",
  orDividerProps,
  emailFieldProps,
  passwordFieldProps,
  registerLinkTextProps,
  registerLinkProps,
  ...props
}: LoginFormProps) {
  const [login, { isLoading, error }] = useLoginUserMutation({});
  const dispatch = useAppDispatch();
  const { error: errorToast } = useAppToast();
  const router = useRouter();
  const startSocialSignIn = useSocialSignIn();
  // Google is hidden while the company has google_signin off.
  const socialAuthProviders = useVisibleSocialProviders(
    configuredSocialProviders,
  );

  return (
    <AppForm<loginValues>
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        mt: "1.5rem",
        ...props,
        ...formProps,
      }}
      apiError={(error && parseApiError(error)) || null}
      initialValues={initialValues}
      validationSchema={loginFormValidationSchema}
      onSubmit={(values) => {
        login(values)
          .unwrap()
          .then((res) => {
            if (res?.data.token) {
              storeLocalStorageData("token", res.data.token);
              storeLocalStorageData("device_id", res.data.device_id);
            }

            dispatch(resetUserStateAction());

            const nextUrl = router.query.next as string;

            if (nextUrl) {
              const target = decodeURIComponent(nextUrl);
              if (target.startsWith("/")) {
                router.push(target);
              } else {
                router.push(getPageRoutes(target));
              }
            } else {
              router.push(getPageRoutes("home"));
            }
          })
          .catch((err) => {
            catchErrorHandler(err, errorToast);
          });
      }}
    >
      <FormField required name="email" label="Email" {...formFieldProps} {...emailFieldProps} />
      <FormField required name="password" type="password" label="Password" {...formFieldProps} {...passwordFieldProps} />
      {error && error.message && (
        <Alert status="error" color="error.900">
          <Text fontWeight={500} color="danger.700">
            {error.message}
          </Text>
        </Alert>
      )}
      {inlineActions ? (
        <Flex w="full" justify="space-between" align="center" mt="1rem" {...actionRowProps}>
          <Button
            isLoading={isLoading || false}
            isDisabled={isLoading || false}
            type="submit"
            {...buttonProps}
          >
            Sign In
          </Button>
          <Link
            as={NextLink}
            href="/forgot-password"
            _hover={{ color: "primary.700" }}
            color="primary.500"
            {...forgotPasswordProps}
          >
            Forgot Password?
          </Link>
        </Flex>
      ) : (
        <>
          <Link
            as={NextLink}
            href="/forgot-password"
            ml="auto"
            mt="1rem"
            _hover={{ color: "primary.700" }}
            color="primary.500"
            {...forgotPasswordProps}
          >
            Forgot Password?
          </Link>
          <Button
            isLoading={isLoading || false}
            isDisabled={isLoading || false}
            mt="1rem"
            w="full"
            type="submit"
            {...buttonProps}
          >
            Log in
          </Button>
        </>
      )}

      {socialAuthProviders && socialAuthProviders.length > 0 && (
        <>
          <Box mt="1rem" fontWeight={500} position="relative" w="full">
            <Divider />
            <AbsoluteCenter
              px={2}
              bg="white"
              fontSize={{ base: "14px", lg: "16px" }}
              {...orDividerProps}
            >
              {orDividerText}
            </AbsoluteCenter>
          </Box>
          <Flex
            direction="row"
            justifyContent="center"
            alignSelf="center"
            gap="1rem"
            mt="0.5rem"
          >
            {socialAuthProviders.map((provider) => (
              <Button
                key={provider.providerName}
                variant="ghost"
                gap="0.5rem"
                onClick={() => {
                  const verifyPath = `/social/${provider.providerName}/verify`;
                  const origin = window?.location?.origin || "";
                  const nextParam = (router.query.next as string) || "";
                  const next = nextParam
                    ? `${verifyPath}?next=${encodeURIComponent(nextParam)}`
                    : verifyPath;
                  startSocialSignIn(provider.providerName, `${origin}${next}`);
                }}
                {...socialButtonProps}
              >
                <Image
                  src={provider.providerSvg}
                  alt={provider.providerName}
                  boxSize="24px"
                  flexShrink={0}
                />
                {showSocialProviderLabel && (
                  <Text as="span" textTransform="capitalize">
                    {provider.providerName}
                  </Text>
                )}
              </Button>
            ))}
          </Flex>
        </>
      )}

      {!hideRegisterLink && (
        <>
          <Divider my="1rem" />
          <Text mx="auto" fontWeight={500} {...registerLinkTextProps}>
            Don&apos;t have an account?{" "}
            <Link
              href="/register"
              color="primary.500"
              _hover={{ color: "primary.700" }}
              {...registerLinkProps}
            >
              Register
            </Link>
          </Text>
        </>
      )}
    </AppForm>
  );
}

export default LoginForm;
